
const libPictProvider = require('pict-provider');
const libRoleDates = require('../access/RoleAssignment-Dates.js');

/**
 * RoleAssignment — the "Roles & Groups" + "Overrides" tabs for a User subject.
 *
 * This is the per-user MEMBERSHIP layer ("User Role Group"): which roles/permission-groups a user holds, in
 * which context. It is the rich-role binding, kept separate from the simple User.IDRole 0-5 base level (which
 * stays in user management). Reads `Permissions/Roles/For/User/<id>/All/Contexts`; assigns via
 * `PUT Permissions/Role/For/User` (Customer -> UserRoleJoin, Project/Lab -> UserContextRole, with optional
 * start and end days, taken in the project's or customer's time zone); removes via DELETE. The Overrides tab manages the user's own UserPermission grants/denies
 * (highest precedence) for the current context.
 *
 * Subject + context come from the AccessWorkspace provider; this owns AppData.AccessWorkspace.Assignment.
 */
class RoleAssignmentProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get workspace()
	{
		return this.pict.providers.AccessWorkspace;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get assignment()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Assignment)
		{
			tmpWorkspaceState.Assignment =
			{
				UserID: 0, Context: '', IDContext: 0, Loading: false, Loaded: false, Error: '', Saving: false,
				Memberships: [], Overrides: [], AssignRoleID: '', StartDraft: '', ExpirationDraft: '', TimeZone: '', OverrideHash: '', OverrideAllow: 'on',
				LoadingSlot: [], ErrorSlot: [], MembershipsSlot: [], MembershipEmptySlot: [], AssignSlot: [], ExpirationSlot: [],
				RoleOptions: [], OverridesSlot: [], OverrideEmptySlot: [],
			};
		}
		return tmpWorkspaceState.Assignment;
	}

	// --- REST ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			// The rest client exposes deleteEntity(entity, id) (there is no raw deleteJSON). Entity/<id> paths
			// go through it; the custom Permissions/Role/.../For/User/... unassign path goes direct.
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch)
			{
				this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody));
				return;
			}
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) =>
				{
					if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; }
					pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({}));
				})
				.catch(reject);
		});
	}

	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 2000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// --- Load ---

	/**
	 * Load the user's role memberships (all contexts) + their permission overrides for the current context.
	 * No-op for a non-user subject.
	 * @param {Boolean} [pForce]
	 */
	async loadAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord || tmpWorkspaceState.SubjectKind !== 'user')
		{
			return;
		}
		const tmpID = Number(tmpWorkspaceState.SubjectID);
		const tmpContext = tmpWorkspaceState.Context;
		const tmpIDContext = Number(tmpWorkspaceState.IDContext);
		const tmpAssignment = this.assignment;
		if (tmpAssignment.Loaded && !pForce && tmpAssignment.UserID === tmpID &&
			tmpAssignment.Context === tmpContext && tmpAssignment.IDContext === tmpIDContext)
		{
			this._render();
			return;
		}
		tmpAssignment.UserID = tmpID;
		tmpAssignment.Context = tmpContext;
		tmpAssignment.IDContext = tmpIDContext;
		tmpAssignment.Loaded = false;
		tmpAssignment.Error = '';
		tmpAssignment.Loading = true;
		this._render();
		try
		{
			const tmpResults = await Promise.all(
				[
					this._getJSON(`Permissions/Roles/For/User/${tmpID}/All/Contexts`),
					tmpIDContext ? this._getFilteredAll('UserPermissions', `FBV~IDUser~EQ~${tmpID}~FBV~Context~EQ~${tmpContext}~FBV~IDContext~EQ~${tmpIDContext}`) : Promise.resolve([]),
					this._loadTimeZoneAsync(tmpContext, tmpIDContext),
				]);
			tmpAssignment.TimeZone = tmpResults[2];
			tmpAssignment.Memberships = this._buildMemberships(tmpResults[0]);
			tmpAssignment.Overrides = (tmpResults[1] || []).map((pRow) =>
				({ IDUserPermission: pRow.IDUserPermission, Hash: pRow.Hash, AllowLabel: pRow.Allowed ? 'Allow' : 'Deny', AllowClass: pRow.Allowed ? 'is-success' : 'is-danger' }));
			tmpAssignment.Loaded = true;
		}
		catch (pError)
		{
			tmpAssignment.Error = `Could not load assignments: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpAssignment.Loading = false;
		this._render();
	}

	/**
	 * The time zone assignment days are read in: the project's, else the customer's, else the browser's.
	 * @param {String} pContext
	 * @param {Number} pIDContext
	 * @return {Promise<String>} an IANA zone, or '' for the browser's.
	 */
	async _loadTimeZoneAsync(pContext, pIDContext)
	{
		const tmpSources = [];
		if ((pContext === 'Project') && pIDContext)
		{
			tmpSources.push(`Project/${pIDContext}`);
		}
		if (this.workspace.idCustomer)
		{
			tmpSources.push(`Customer/${this.workspace.idCustomer}`);
		}
		for (const tmpSource of tmpSources)
		{
			try
			{
				const tmpRecord = await this._getJSON(tmpSource);
				if (tmpRecord && libRoleDates.isValidTimeZone(tmpRecord.Timezone))
				{
					return tmpRecord.Timezone;
				}
			}
			catch (pError)
			{
				// Fall through to the next source; the browser's zone is the last resort.
			}
		}
		return '';
	}

	_buildMemberships(pResponse)
	{
		const tmpTimeZone = this.assignment.TimeZone;
		const tmpNameByID = {};
		(this.workspace.rolesAndGroups || []).forEach((pRole) =>
		{
			tmpNameByID[pRole.IDRole] = pRole;
		});
		const tmpFromContext = (pContext, pIDContext, pIDRole, pExpiration, pStart) =>
		{
			const tmpRole = tmpNameByID[pIDRole] || {};
			const tmpStartDay = libRoleDates.instantToDay(pStart, tmpTimeZone);
			const tmpEndDay = libRoleDates.instantToDay(pExpiration, tmpTimeZone);
			const tmpState = libRoleDates.getWindowState(pStart, pExpiration);
			return {
				Context: pContext,
				IDContext: pIDContext,
				IDRole: pIDRole,
				Label: tmpRole.Name || `Role ${pIDRole}`,
				KindLabel: (tmpRole.Type === 'PermissionGroup') ? 'Permission Group' : 'Role',
				Icon: (tmpRole.Type === 'PermissionGroup') ? 'fas fa-layer-group' : 'fas fa-user-shield',
				ContextLabel: ((Number(pIDContext) > 0 && pContext !== 'Customer') ? `${pContext} ${pIDContext}` : pContext) +
					(tmpStartDay ? ` · from ${tmpStartDay}` : '') + (tmpEndDay ? ` · until ${tmpEndDay}` : ''),
				Expiration: tmpEndDay,
				State: tmpState,
				StateSlot: (tmpState === 'Active') ? [] :
					[ (tmpState === 'Scheduled') ? { Label: 'scheduled', Class: 'is-warning', Title: `Takes effect ${tmpStartDay}` } : { Label: 'expired', Class: 'is-dark', Title: `Ended ${tmpEndDay}` } ],
			};
		};
		const tmpMemberships = [];
		const tmpSeen = new Set();
		(pResponse && pResponse.Contexts ? pResponse.Contexts : []).forEach((pEntry) =>
		{
			if (!pEntry || !pEntry.IDRole)
			{
				return;
			}
			const tmpKey = `${pEntry.Context}-${pEntry.IDContext}-${pEntry.IDRole}`;
			if (tmpSeen.has(tmpKey))
			{
				return;
			}
			tmpSeen.add(tmpKey);
			tmpMemberships.push(tmpFromContext(pEntry.Context, pEntry.IDContext, pEntry.IDRole, pEntry.ExpirationDate, pEntry.StartDate));
		});
		// Customer-level roles (UserRoleJoin) may come back under Roles[] as ids or objects.
		(pResponse && pResponse.Roles ? pResponse.Roles : []).forEach((pRole) =>
		{
			const tmpID = (pRole && typeof pRole === 'object') ? pRole.IDRole : pRole;
			if (!tmpID)
			{
				return;
			}
			const tmpKey = `Customer-${this.workspace.idCustomer}-${tmpID}`;
			if (tmpSeen.has(tmpKey))
			{
				return;
			}
			tmpSeen.add(tmpKey);
			tmpMemberships.unshift(tmpFromContext('Customer', this.workspace.idCustomer, tmpID, null, null));
		});
		return tmpMemberships;
	}

	// --- Form state ---

	setAssignRole(pValue)
	{
		this.assignment.AssignRoleID = pValue || '';
	}
	/**
	 * Draft the first day the assignment applies ("YYYY-MM-DD", or empty for now).
	 * @param {String} pValue
	 */
	setStart(pValue)
	{
		this.assignment.StartDraft = pValue || '';
	}
	setExpiration(pValue)
	{
		this.assignment.ExpirationDraft = pValue || '';
	}
	setOverrideHash(pValue)
	{
		this.assignment.OverrideHash = pValue || '';
	}
	setOverrideAllow(pValue)
	{
		this.assignment.OverrideAllow = pValue || 'on';
	}

	// --- Mutations ---

	/**
	 * Why the drafted start and end days cannot be saved as they are, if they cannot.
	 * @param {Number} pIDRole - the role being assigned
	 * @return {String} a message for the form, or '' when the days are fine.
	 */
	_checkDraftDays(pIDRole)
	{
		const tmpAssignment = this.assignment;
		if (tmpAssignment.StartDraft && tmpAssignment.ExpirationDraft && (tmpAssignment.StartDraft > tmpAssignment.ExpirationDraft))
		{
			return 'The start date has to be on or before the end date.';
		}
		if (tmpAssignment.StartDraft && (tmpAssignment.StartDraft > libRoleDates.todayIn(tmpAssignment.TimeZone)))
		{
			// Assigning a role the user already holds here updates that assignment, so a future start
			// would take the role away until then.
			const tmpHeld = tmpAssignment.Memberships.find((pMembership) =>
				(pMembership.Context === tmpAssignment.Context) && (Number(pMembership.IDContext) === Number(tmpAssignment.IDContext)) &&
				(Number(pMembership.IDRole) === Number(pIDRole)) && (pMembership.State === 'Active'));
			if (tmpHeld)
			{
				return `This user already has ${tmpHeld.Label} here. A future start date would remove it until then. ` +
					'Leave the start date empty to change only the end date, or remove the role first.';
			}
		}
		return '';
	}

	/**
	 * Assign the picked role/group to the user in the workspace's current context (optional start and end days
	 * for non-Customer contexts), via PUT Permissions/Role/For/User.
	 */
	async assignAsync()
	{
		const tmpAssignment = this.assignment;
		const tmpRoleID = Number(tmpAssignment.AssignRoleID);
		if (!(tmpRoleID > 0) || tmpAssignment.Saving)
		{
			return;
		}
		if (!tmpAssignment.IDContext)
		{
			tmpAssignment.Error = `Enter a ${tmpAssignment.Context} ID in the context bar first.`;
			this._render();
			return;
		}
		const tmpDayProblem = (tmpAssignment.Context !== 'Customer') ? this._checkDraftDays(tmpRoleID) : '';
		if (tmpDayProblem)
		{
			tmpAssignment.Error = tmpDayProblem;
			this._render();
			return;
		}
		tmpAssignment.Saving = true;
		tmpAssignment.Error = '';
		this._render();
		const tmpPayload =
		{
			IDRole: tmpRoleID,
			IDUser: tmpAssignment.UserID,
			Context: tmpAssignment.Context,
			IDContext: tmpAssignment.IDContext,
		};
		// Start and end days are only valid for non-Customer contexts (the server rejects them for Customer).
		if (tmpAssignment.Context !== 'Customer')
		{
			if (tmpAssignment.StartDraft)
			{
				tmpPayload.StartDate = libRoleDates.dayStartToInstant(tmpAssignment.StartDraft, tmpAssignment.TimeZone);
			}
			if (tmpAssignment.ExpirationDraft)
			{
				tmpPayload.ExpirationDate = libRoleDates.dayEndToInstant(tmpAssignment.ExpirationDraft, tmpAssignment.TimeZone);
			}
		}
		try
		{
			await this._putJSON('Permissions/Role/For/User', tmpPayload);
			tmpAssignment.AssignRoleID = '';
			tmpAssignment.StartDraft = '';
			tmpAssignment.ExpirationDraft = '';
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpAssignment.Error = `Could not assign: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpAssignment.Saving = false;
		this._render();
	}

	/**
	 * Remove a membership (DELETE Permissions/Role/.../For/User/...).
	 * @param {Number} pIDRole
	 * @param {String} pContext
	 * @param {Number} pIDContext
	 */
	async removeMembershipAsync(pIDRole, pContext, pIDContext)
	{
		const tmpAssignment = this.assignment;
		if (tmpAssignment.Saving)
		{
			return;
		}
		tmpAssignment.Saving = true;
		tmpAssignment.Error = '';
		this._render();
		try
		{
			await this._deleteJSON(`Permissions/Role/${pIDRole}/For/User/${tmpAssignment.UserID}/In/${pContext}/${pIDContext}`);
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpAssignment.Error = `Could not remove: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpAssignment.Saving = false;
		this._render();
	}

	/**
	 * Add a user permission override (Grant/Deny .../To/User/...) in the current context.
	 */
	async addOverrideAsync()
	{
		const tmpAssignment = this.assignment;
		const tmpHash = String(tmpAssignment.OverrideHash || '').trim();
		if (!tmpHash || tmpAssignment.Saving)
		{
			return;
		}
		if (!tmpAssignment.IDContext)
		{
			tmpAssignment.Error = `Enter a ${tmpAssignment.Context} ID in the context bar first.`;
			this._render();
			return;
		}
		tmpAssignment.Saving = true;
		tmpAssignment.Error = '';
		this._render();
		const tmpVerb = (tmpAssignment.OverrideAllow === 'off') ? 'Deny' : 'Grant';
		try
		{
			await this._getJSON(`Permissions/${tmpVerb}/${tmpHash}/To/User/${tmpAssignment.UserID}/For/${tmpAssignment.Context}/${tmpAssignment.IDContext}`);
			tmpAssignment.OverrideHash = '';
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpAssignment.Error = `Could not add override: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpAssignment.Saving = false;
		this._render();
	}

	/**
	 * Remove a user permission override by deleting its row.
	 * @param {Number} pIDUserPermission
	 */
	async removeOverrideAsync(pIDUserPermission)
	{
		const tmpAssignment = this.assignment;
		if (tmpAssignment.Saving)
		{
			return;
		}
		tmpAssignment.Saving = true;
		tmpAssignment.Error = '';
		this._render();
		try
		{
			await this._deleteJSON(`UserPermission/${pIDUserPermission}`);
			await this.loadAsync(true);
		}
		catch (pError)
		{
			tmpAssignment.Error = `Could not remove override: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpAssignment.Saving = false;
		this._render();
	}

	// --- Derive ---

	deriveAssignment()
	{
		const tmpAssignment = this.assignment;
		const tmpEditable = !!this.workspace.isRoleManager;
		tmpAssignment.LoadingSlot = tmpAssignment.Loading ? [ {} ] : [];
		tmpAssignment.ErrorSlot = tmpAssignment.Error ? [ { Error: tmpAssignment.Error } ] : [];

		tmpAssignment.Memberships = tmpAssignment.Memberships.map((pMembership) =>
			Object.assign({}, pMembership, { RemoveSlot: tmpEditable ? [ pMembership ] : [] }));
		tmpAssignment.MembershipsSlot = tmpAssignment.Memberships.length ? [ {} ] : [];
		tmpAssignment.MembershipEmptySlot = (!tmpAssignment.Loading && !tmpAssignment.Memberships.length) ? [ {} ] : [];

		tmpAssignment.Overrides = tmpAssignment.Overrides.map((pOverride) =>
			Object.assign({}, pOverride, { RemoveSlot: tmpEditable ? [ { IDUserPermission: pOverride.IDUserPermission } ] : [] }));
		tmpAssignment.OverridesSlot = tmpAssignment.Overrides.length ? [ {} ] : [];
		tmpAssignment.OverrideEmptySlot = (!tmpAssignment.Loading && !tmpAssignment.Overrides.length) ? [ {} ] : [];

		if (tmpEditable)
		{
			tmpAssignment.RoleOptions = [ { IDRole: '', Label: 'Pick a role or group…' } ].concat(
				(this.workspace.rolesAndGroups || [])
					.map((pRole) => ({ IDRole: pRole.IDRole, Label: `${pRole.Name || `Role ${pRole.IDRole}`}${pRole.Type === 'PermissionGroup' ? ' (group)' : ''}` }))
					.sort((pA, pB) => String(pA.Label).localeCompare(String(pB.Label))));
			tmpAssignment.AssignSlot = [ { Context: tmpAssignment.Context } ];
			// Start and end days only offered for non-Customer contexts.
			tmpAssignment.ExpirationSlot = (tmpAssignment.Context !== 'Customer') ?
				[ { StartDraft: tmpAssignment.StartDraft, ExpirationDraft: tmpAssignment.ExpirationDraft, Today: libRoleDates.todayIn(tmpAssignment.TimeZone) } ] : [];
		}
		else
		{
			tmpAssignment.RoleOptions = [];
			tmpAssignment.AssignSlot = [];
			tmpAssignment.ExpirationSlot = [];
		}
	}

	_render()
	{
		if (this.pict.views.AccessWorkspace)
		{
			this.pict.views.AccessWorkspace.render();
		}
	}
}

module.exports = RoleAssignmentProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'RoleAssignment',
	AutoInitialize: true,
	AutoInitializeOrdinal: 4,
};
